
import { Helmet } from 'react-helmet-async'
import { getCanonicalUrl } from '../utils/canonical'
import { 
  Globe,
  Users,
  Lightbulb,
  Heart,
  
  MapPin,
  Laptop,
  
} from 'lucide-react'

export default function ProjectsPage() {
  const globalInitiatives = [
    {
      name: 'National Digital Inclusion Alliance (USA)',
      description: 'The National Digital Inclusion Alliance (NDIA) is a unified voice for digital equity in the United States. Rather than providing direct services, the organization acts as an influential network that supports hundreds of local affiliates—from libraries to community organizations—and represents their interests at the policy level. The NDIA advocates for affordable broadband access, suitable devices, and digital literacy training for all citizens, ensuring that community voices are heard in national policy debates.',
      category: 'Global Digital Inclusion',
      location: 'USA',
      icon: Globe
    },
    {
      name: 'Online Centres Network (UK)',
      description: 'In the UK, the Good Things Foundation operates the Online Centres Network, an impressive network of around 5,000 community centers. These hubs provide free or low-cost access to computers, the internet, and foundational digital skills training for people who are offline or have limited digital skills. By bringing technology to familiar places like libraries and neighborhood centers, the foundation creates a welcoming environment where everyone can learn the digital basics essential for modern life.',
      category: 'Global Digital Inclusion',
      location: 'UK',
      icon: Globe
    }
  ]

  const techAccessibilityOrgs = [
    {
      name: 'Knowbility (USA)',
      description: 'Knowbility is a nonprofit organization whose mission is to make technology accessible to all people, regardless of their abilities. They offer comprehensive training, consulting, and testing services to help companies and developers create accessible websites and applications. A major highlight is their "Accessibility Internet Rally" (AIR), a competition where teams of web professionals develop accessible websites for nonprofits, combining practical skill-building with community impact.',
      category: 'Accessible Tech & Web Inclusion',
      location: 'USA',
      icon: Laptop
    },
    {
      name: 'Access Heroes',
      description: 'The Access Heroes initiative aims to build a community of digital accessibility champions. Through hands-on training programs and a system of digital badges that certify acquired skills, the organization empowers individuals to become "accessibility heroes" within their own companies and projects. Their approach focuses on raising awareness and giving developers, designers, and content creators the tangible tools they need to integrate inclusion into the digital development process from the very beginning.',
      category: 'Accessible Tech & Web Inclusion',
      location: 'Global',
      icon: Laptop
    },
    {
      name: 'AnySurfer (Belgium)',
      description: 'AnySurfer is a Belgian initiative that has been championing a more accessible internet for over 20 years. Its most well-known tool is the AnySurfer label, a quality seal awarded to websites that are proven to meet international WCAG standards. In addition to granting this label, the organization offers audits, training, and consulting to help public and private entities implement digital accessibility, making the web better and more user-friendly for people with disabilities and everyone else.',
      category: 'Accessible Tech & Web Inclusion',
      location: 'Belgium',
      icon: Laptop
    }
  ]

  const communityProjects = [
    {
      name: 'Fundación Proacceso (Mexico)',
      description: 'The Fundación Proacceso fights the digital divide in Mexico by establishing a network of digital education centers (RIAs) in 55 low-income communities. These centers not only provide access to computers and the internet but also offer a wide range of courses—from basic computer literacy to online diplomas that create professional opportunities. By creating physical learning spaces, the foundation ensures that access to technology is paired with high-quality education and personal support.',
      category: 'Community Inclusion & Infrastructure',
      location: 'Mexico',
      icon: Users
    },
    {
      name: 'Tech Herfrica (Nigeria & Africa-wide)',
      description: 'The organization Tech Herfrica is dedicated to advancing the digital and financial inclusion of women in rural and underserved areas across Africa. A core part of its work is the "HerLocal" platform, which helps female entrepreneurs access digital markets and financial services. This is complemented by targeted digital literacy programs that equip women with the necessary skills to confidently use technology for their businesses and personal growth, thereby fostering economic independence.',
      category: 'Community Inclusion & Infrastructure',
      location: 'Nigeria & Africa',
      icon: Users
    }
  ]

  const innovativeTools = [
    {
      name: 'Wheelmap.org',
      description: 'Initiated by the German nonprofit SOZIALHELDEN, Wheelmap.org is a free, open-source world map for finding and marking wheelchair-accessible places. The project harnesses the power of the community: anyone can join and rate locations according to a simple traffic-light system (green: fully accessible, yellow: partially accessible, red: not accessible). This crowdsourcing model has led to the mapping of millions of places worldwide, giving people with mobility impairments a valuable tool to plan their daily lives with greater autonomy.',
      category: 'Innovative Access & Mapping Tools',
      location: 'Germany (Global)',
      icon: MapPin
    }
  ]

  const youthInnovations = [
    {
      name: 'Audemy',
      description: 'Started by a high school founder, Audemy is a nonprofit gaming platform designed specifically for visually impaired students. The initiative uses artificial intelligence and a team of volunteers to transform educational content into interactive and engaging audio games. Audemy addresses the lack of modern, appealing learning materials for this audience and impressively demonstrates how youth innovation and technology can be used to make education more equitable.',
      category: 'Youth & Tech Innovations',
      location: 'USA',
      icon: Lightbulb
    }
  ]

  const allProjects = [
    ...globalInitiatives,
    ...techAccessibilityOrgs,
    ...communityProjects,
    ...innovativeTools,
    ...youthInnovations
  ]

  const categories = [
    {
      title: 'Global Digital Inclusion Initiatives',
      description: 'These organizations operate on a national level to drive systemic change through policy advocacy, network building, and strengthening local communities.',
      projects: globalInitiatives,
      color: 'blue'
    },
    {
      title: 'Accessible Tech & Web Inclusion Organizations',
      description: 'The focus of this group is on technical accessibility. They train developers, audit digital products, and advocate for compliance with international standards like the Web Content Accessibility Guidelines (WCAG).',
      projects: techAccessibilityOrgs,
      color: 'green'
    },
    {
      title: 'Community Inclusion & Infrastructure Projects',
      description: 'These projects focus on bringing digital infrastructure and education directly to underserved communities and empowering specific demographics.',
      projects: communityProjects,
      color: 'purple'
    },
    {
      title: 'Innovative Access & Mapping Tools',
      description: 'Sometimes the best solution is a smart, community-driven tool that solves a specific accessibility problem.',
      projects: innovativeTools,
      color: 'orange'
    },
    {
      title: 'Youth & Tech Innovations',
      description: 'Young founders bring fresh perspectives and leverage the latest technologies to close inclusion gaps.',
      projects: youthInnovations,
      color: 'pink'
    }
  ]

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return {
          bg: 'bg-blue-50 border-blue-200',
          icon: 'bg-blue-100 text-blue-600',
          header: 'text-blue-900'
        }
      case 'green':
        return {
          bg: 'bg-green-50 border-green-200',
          icon: 'bg-green-100 text-green-600',
          header: 'text-green-900'
        }
      case 'purple':
        return {
          bg: 'bg-purple-50 border-purple-200',
          icon: 'bg-purple-100 text-purple-600',
          header: 'text-purple-900'
        }
      case 'orange':
        return {
          bg: 'bg-orange-50 border-orange-200',
          icon: 'bg-orange-100 text-orange-600',
          header: 'text-orange-900'
        }
      case 'pink':
        return {
          bg: 'bg-pink-50 border-pink-200',
          icon: 'bg-pink-100 text-pink-600',
          header: 'text-pink-900'
        }
      default:
        return {
          bg: 'bg-gray-50 border-gray-200',
          icon: 'bg-gray-100 text-gray-600',
          header: 'text-gray-900'
        }
    }
  }

  return (
    <>
      <Helmet>
        <title>Global Inclusion Projects: 8 Initiatives for Digital Accessibility & Internet Equity | achecker.ca</title>
        <meta name="description" content="Discover 8 pioneering nonprofit initiatives from around the world dedicated to digital accessibility and internet equity. From the US to Nigeria, get inspired by projects that connect technology, education, and community impact." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('/projects')} />
      </Helmet>

      <div className="space-y-12">
        {/* Hero Section */}
        <section className="text-center py-12 bg-gradient-to-br from-indigo-50 to-purple-100 rounded-2xl">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Global Inclusion Projects for Digital Accessibility & Internet Equity
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
            Discover pioneering nonprofit initiatives from around the world dedicated to digital accessibility and internet equity. These projects connect technology, education, and community impact to build a more inclusive digital future for everyone.
          </p>
          <div className="flex justify-center">
            <div className="inline-flex items-center px-6 py-3 bg-indigo-100 text-indigo-700 rounded-lg font-medium">
              <Heart className="w-5 h-5 mr-2" />
              {allProjects.length} Inspiring Initiatives Worldwide
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              In an increasingly connected world, access to the internet and digital technologies is no longer a luxury but a fundamental prerequisite for education, economic participation, and civic engagement. Yet, significant barriers continue to exclude millions of people from these opportunities.
            </p>
            <p className="text-gray-700 mb-6">
              The "digital divide" is a multi-faceted issue, encompassing a lack of infrastructure, insufficient digital literacy, prohibitive costs, and digital content that is inaccessible to people with disabilities. Fortunately, numerous nonprofit organizations and initiatives worldwide are tackling these challenges with impressive dedication and innovative approaches.
            </p>
            <p className="text-gray-700">
              They work at the intersection of technology, education, and policy to build a more inclusive digital future for everyone. This article highlights eight pioneering projects that serve as an inspiration and a model for digital accessibility and internet equity.
            </p>
          </div>
        </section>

        {/* Project Categories */}
        {categories.map((category, categoryIndex) => (
          <section key={categoryIndex}>
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{category.title}</h2>
              <p className="text-lg text-gray-700">{category.description}</p>
            </div>
            
            <div className="space-y-6">
              {category.projects.map((project, projectIndex) => {
                const colors = getColorClasses(category.color)
                const Icon = project.icon
                
                return (
                  <div key={projectIndex} className={`rounded-lg p-8 border ${colors.bg}`}>
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.icon}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className={`text-xl font-bold mb-2 ${colors.header}`}>
                              {project.name}
                            </h3>
                            <div className="flex items-center text-sm text-gray-600 mb-3">
                              <MapPin className="w-4 h-4 mr-1" />
                              {project.location}
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-700 leading-relaxed">
                          {project.description}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        ))}

        {/* Why These Projects Matter */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Why These Projects Matter</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              The initiatives featured here are more than just individual success stories. They illustrate how diverse and impactful the movement for digital inclusion can be. Their importance lies in several key aspects:
            </p>
            
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  They connect theory and practice
                </h4>
                <p className="text-gray-700">
                  They combine technical expertise with education, policy advocacy, and direct local impact. Instead of getting stuck in abstract theory, they create measurable results in the communities they serve.
                </p>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  They bridge critical gaps
                </h4>
                <p className="text-gray-700">
                  These projects address the full spectrum of the digital divide—whether it's physical access to places (Wheelmap), a lack of internet infrastructure (Fundación Proacceso), educational deficits (Tech Herfrica), or the need for technical web accessibility (Knowbility, AnySurfer).
                </p>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  They provide tangible models for action
                </h4>
                <p className="text-gray-700">
                  They serve as active examples that inspire others to act. Their work is transparent, often community-driven, and provides valuable blueprints that can be linked to, used as inspiration for new projects, or serve as partners for socially responsible companies.
                </p>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 mt-8 font-medium">
              By viewing technology as a tool for human empowerment rather than an end in itself, these organizations make an indispensable contribution to a more just and inclusive digital society.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center py-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">
            Inspired to Make a Difference?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Start with accessibility testing on your own website and join the global movement for digital inclusion
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/scan"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-indigo-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <Laptop className="w-5 h-5 mr-2" />
              Test Your Website
            </a>
            <a
              href="/"
              className="inline-flex items-center justify-center px-8 py-3 bg-indigo-700 text-white rounded-lg font-semibold hover:bg-indigo-800 transition-colors border border-indigo-500"
            >
              <Globe className="w-5 h-5 mr-2" />
              Learn More About Accessibility
            </a>
          </div>
        </section>
      </div>
    </>
  )
}
